import * as React from "react";
import { getBadges, markOrdersSeen, type AdminBadges } from "@/api/admin";

/** How often the menu asks again while the operator stays on one screen. */
export const BADGE_POLL_MS = 60_000;

/**
 * How each count is cleared. Keyed by the type, so a count added to
 * `AdminBadges` does not compile until it says how it clears.
 */
const CLEAR: Record<keyof AdminBadges, () => Promise<AdminBadges>> = {
  orders: () => markOrdersSeen(),
};

/** Whether the tab is in view. One question, asked one way. */
const inView = () => !document.hidden;

/**
 * The menu's counts, kept current.
 *
 * Asked on every visit to a screen -- `visit` changes on each navigation,
 * clicking the link of the screen already open included -- once a minute
 * while the tab is in view, and again when the tab comes back into view. A
 * tab in the background asks nothing.
 *
 * `seen` names the count the current screen clears: the menu passes it only
 * on a link's own unfiltered list, so opening one order from a customer's
 * page clears nothing. The worker answers that look with the counts as of
 * it, so the badge the operator is looking at is gone on arrival. A failed
 * ask keeps the last counts: a menu is no place to report a network blip.
 */
export function useBadges(
  visit: string,
  seen: keyof AdminBadges | undefined,
): AdminBadges | null {
  const [badges, setBadges] = React.useState<AdminBadges | null>(null);

  React.useEffect(() => {
    let alive = true;
    const take = async (ask: () => Promise<AdminBadges>) => {
      try {
        const next = await ask();
        if (alive) setBadges(next);
      } catch {
        // Kept as they were; see above.
      }
    };
    void take(seen ? CLEAR[seen] : getBadges);
    const timer = window.setInterval(() => {
      if (inView()) void take(getBadges);
    }, BADGE_POLL_MS);
    const onShow = () => {
      if (inView()) void take(getBadges);
    };
    document.addEventListener("visibilitychange", onShow);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onShow);
    };
  }, [visit, seen]);

  return badges;
}
